/**
 * Sound library — SFX (shuffle / flip) and Ambient music.
 * All synthesised via Web Audio API. No external audio files.
 * User preferences persisted in localStorage.
 */

const SFX_KEY = "arcana.sfx.enabled";
const MUSIC_KEY = "arcana.music.enabled";
const TRACK_KEY = "arcana.music.track";

let audioCtx = null;
let sfxEnabled =
  typeof window !== "undefined"
    ? window.localStorage.getItem(SFX_KEY) !== "false"
    : true;
let musicEnabled =
  typeof window !== "undefined"
    ? window.localStorage.getItem(MUSIC_KEY) === "true"  // default OFF
    : false;

// Ambient (drone) node graph
let ambientMaster = null;
let ambientNodes = null;
const AMBIENT_BASE_GAIN = 0.06;

/**
 * Three hand-tuned ambient presets. Each shapes the same synth graph (detuned sines
 * + slow LFO + noise "air") with different roots, tempos and colours.
 */
export const AMBIENT_TRACKS = [
  {
    id: "nebuleuse",
    name_fr: "Nébuleuse",
    name_en: "Nebula",
    notes: [
      { freq: 110, detune: -6, gain: 0.18 },
      { freq: 165, detune: +4, gain: 0.14 },
      { freq: 220, detune: -3, gain: 0.10 },
      { freq: 277, detune: +7, gain: 0.06 },
    ],
    lfoBase: 0.06,
    airLp: 380,
    airGain: 0.04,
  },
  {
    id: "aube",
    name_fr: "Aube",
    name_en: "Dawn",
    notes: [
      { freq: 146.83, detune: -4, gain: 0.14 }, // D3
      { freq: 220,    detune: +5, gain: 0.12 }, // A3
      { freq: 293.66, detune: -2, gain: 0.10 }, // D4
      { freq: 440,    detune: +9, gain: 0.05 }, // A4 — airy top
    ],
    lfoBase: 0.10,
    airLp: 620,
    airGain: 0.03,
  },
  {
    id: "riviere",
    name_fr: "Rivière",
    name_en: "River",
    notes: [
      { freq: 73.42,  detune: -8, gain: 0.20 }, // D2 — deep floor
      { freq: 110,    detune: +3, gain: 0.15 }, // A2
      { freq: 174.61, detune: -5, gain: 0.11 }, // F3 — melancholic third
      { freq: 220,    detune: +6, gain: 0.07 }, // A3
    ],
    lfoBase: 0.03,
    airLp: 260,
    airGain: 0.05,
  },
];

let currentTrackId =
  typeof window !== "undefined"
    ? window.localStorage.getItem(TRACK_KEY) || AMBIENT_TRACKS[0].id
    : AMBIENT_TRACKS[0].id;
if (!AMBIENT_TRACKS.find((t) => t.id === currentTrackId)) {
  currentTrackId = AMBIENT_TRACKS[0].id;
}

const currentTrack = () =>
  AMBIENT_TRACKS.find((t) => t.id === currentTrackId) || AMBIENT_TRACKS[0];

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    audioCtx = new AC();
  }
  if (audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
};

const noiseBuffer = (ctx, duration) => {
  const rate = ctx.sampleRate;
  const buffer = ctx.createBuffer(1, rate * duration, rate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
};

// ---------- SFX ----------
export const playFlip = () => {
  if (!sfxEnabled) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 0.18);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(2000, now);
  bp.frequency.exponentialRampToValueAtTime(700, now + 0.14);
  bp.Q.value = 1.4;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.35, now + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
  src.connect(bp).connect(gain).connect(ctx.destination);
  src.start(now);
  src.stop(now + 0.18);
};

/**
 * Cutting the deck: a single crisp "riffle" — a short low thud as the half lifts,
 * then a quick bright slide as it lands on the pile. Distinct from the multi-burst shuffle.
 */
export const playCut = () => {
  if (!sfxEnabled) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  // 1) soft low thud (lifting the half)
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(160, now);
  osc.frequency.exponentialRampToValueAtTime(70, now + 0.12);
  const og = ctx.createGain();
  og.gain.setValueAtTime(0.0001, now);
  og.gain.exponentialRampToValueAtTime(0.28, now + 0.006);
  og.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
  osc.connect(og).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.15);
  // 2) bright slide as the cards land (single long noise sweep, high-pass)
  const t1 = now + 0.16;
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 0.3);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.setValueAtTime(900, t1);
  hp.frequency.exponentialRampToValueAtTime(3200, t1 + 0.22);
  hp.Q.value = 0.8;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t1);
  g.gain.exponentialRampToValueAtTime(0.2, t1 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t1 + 0.26);
  src.connect(hp).connect(g).connect(ctx.destination);
  src.start(t1);
  src.stop(t1 + 0.3);
};

/**
 * A gentle two-tone chime signalling that the querent should pick a card.
 * Bell-like sound: a fundamental + fifth harmonic + slight tail resonance.
 */
export const playChime = () => {
  if (!sfxEnabled) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.35, now + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 1.6);
  master.connect(ctx.destination);

  // Two-tone bell: 880 Hz (A5) then 1174 Hz (D6) — a lifting minor third
  const notes = [
    { f: 880,  start: 0.0,  dur: 1.4, gain: 0.6 },
    { f: 1174, start: 0.14, dur: 1.4, gain: 0.35 },
    // Higher shimmer partial
    { f: 1760, start: 0.02, dur: 0.9, gain: 0.14 },
  ];
  notes.forEach(({ f, start, dur, gain }) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now + start);
    g.gain.exponentialRampToValueAtTime(gain, now + start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(g).connect(master);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.05);
  });
};

export const playShuffle = () => {
  if (!sfxEnabled) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const bursts = 4;
  const spacing = 0.09;
  for (let i = 0; i < bursts; i++) {
    const t0 = now + i * spacing;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 0.16);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(1400 + Math.random() * 900, t0);
    bp.frequency.exponentialRampToValueAtTime(500 + Math.random() * 400, t0 + 0.14);
    bp.Q.value = 1.2;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    src.connect(bp).connect(gain).connect(ctx.destination);
    src.start(t0);
    src.stop(t0 + 0.16);
  }
};

// ---------- Ambient music ----------
// Slow drone built from a few detuned sine waves at low pitch + a very quiet
// filtered noise "air" layer. Volume fades in gently and can be ducked during reveal.
// Shape (notes/LFO/air filter) is driven by the currently-selected AMBIENT_TRACK.
const buildAmbient = (ctx) => {
  const track = currentTrack();
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(ctx.destination);

  const oscs = track.notes.map((n) => {
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = n.freq;
    o.detune.value = n.detune;
    const g = ctx.createGain();
    g.gain.value = n.gain;
    // Very slow LFO on gain for gentle breathing
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = track.lfoBase + Math.random() * 0.04;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = n.gain * 0.35;
    lfo.connect(lfoGain).connect(g.gain);
    o.connect(g).connect(master);
    o.start();
    lfo.start();
    return { o, g, lfo, lfoGain };
  });

  // Soft "air" layer — pink-ish noise heavily low-passed
  const noise = ctx.createBufferSource();
  const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < data.length; i++) {
    // simple pink-ish via IIR
    const white = Math.random() * 2 - 1;
    last = (last * 0.98 + white * 0.02);
    data[i] = last * 4;
  }
  noise.buffer = buf;
  noise.loop = true;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = track.airLp;
  const ng = ctx.createGain();
  ng.gain.value = track.airGain;
  noise.connect(lp).connect(ng).connect(master);
  noise.start();

  return { master, oscs, noise, ng, lp };
};

const teardownAmbient = () => {
  if (!ambientNodes) return;
  const ctx = getCtx();
  try {
    ambientNodes.oscs.forEach(({ o, lfo }) => {
      try { o.stop(); } catch (_) { /* already stopped */ }
      try { lfo.stop(); } catch (_) { /* already stopped */ }
    });
    try { ambientNodes.noise.stop(); } catch (_) { /* already stopped */ }
    if (ctx) {
      ambientNodes.master.disconnect();
    }
  } catch (_) {
    /* best-effort teardown */
  }
  ambientNodes = null;
  ambientMaster = null;
};

export const startAmbient = () => {
  if (!musicEnabled) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (!ambientMaster) {
    ambientNodes = buildAmbient(ctx);
    ambientMaster = ambientNodes.master;
  }
  const now = ctx.currentTime;
  ambientMaster.gain.cancelScheduledValues(now);
  ambientMaster.gain.setValueAtTime(ambientMaster.gain.value, now);
  ambientMaster.gain.linearRampToValueAtTime(AMBIENT_BASE_GAIN, now + 3.5);
};

export const duckAmbient = () => {
  if (!ambientMaster) return;
  const ctx = getCtx();
  const now = ctx.currentTime;
  ambientMaster.gain.cancelScheduledValues(now);
  ambientMaster.gain.setValueAtTime(ambientMaster.gain.value, now);
  ambientMaster.gain.linearRampToValueAtTime(AMBIENT_BASE_GAIN * 0.15, now + 1.4);
};

export const unduckAmbient = () => {
  if (!ambientMaster || !musicEnabled) return;
  const ctx = getCtx();
  const now = ctx.currentTime;
  ambientMaster.gain.cancelScheduledValues(now);
  ambientMaster.gain.setValueAtTime(ambientMaster.gain.value, now);
  ambientMaster.gain.linearRampToValueAtTime(AMBIENT_BASE_GAIN, now + 2.5);
};

export const stopAmbient = (fadeSec = 2) => {
  if (!ambientMaster) return;
  const ctx = getCtx();
  const now = ctx.currentTime;
  ambientMaster.gain.cancelScheduledValues(now);
  ambientMaster.gain.setValueAtTime(ambientMaster.gain.value, now);
  ambientMaster.gain.linearRampToValueAtTime(0, now + fadeSec);
  const nodes = ambientNodes;
  setTimeout(() => {
    if (ambientNodes === nodes) teardownAmbient();
  }, fadeSec * 1000 + 100);
};

// ---------- Track selection ----------
export const getAmbientTrackId = () => currentTrackId;
export const getAmbientTrack = () => currentTrack();

export const setAmbientTrackId = (id) => {
  const found = AMBIENT_TRACKS.find((t) => t.id === id);
  if (!found) return;
  currentTrackId = id;
  try { window.localStorage.setItem(TRACK_KEY, id); } catch { /* ignore */ }
  // If music is currently playing, rebuild the graph with the new preset.
  if (musicEnabled && ambientMaster) {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const oldNodes = ambientNodes;
    // Fade out old
    ambientMaster.gain.cancelScheduledValues(now);
    ambientMaster.gain.setValueAtTime(ambientMaster.gain.value, now);
    ambientMaster.gain.linearRampToValueAtTime(0, now + 1.0);
    setTimeout(() => {
      // Stop old nodes and build fresh ones
      if (ambientNodes === oldNodes) {
        try {
          oldNodes.oscs.forEach(({ o, lfo }) => {
            try { o.stop(); } catch (_) { /* ignore */ }
            try { lfo.stop(); } catch (_) { /* ignore */ }
          });
          try { oldNodes.noise.stop(); } catch (_) { /* ignore */ }
          oldNodes.master.disconnect();
        } catch (_) { /* ignore */ }
        ambientNodes = buildAmbient(ctx);
        ambientMaster = ambientNodes.master;
        const n = ctx.currentTime;
        ambientMaster.gain.setValueAtTime(0, n);
        ambientMaster.gain.linearRampToValueAtTime(AMBIENT_BASE_GAIN, n + 2.5);
      }
    }, 1100);
  }
};

export const cycleAmbientTrack = () => {
  const idx = AMBIENT_TRACKS.findIndex((t) => t.id === currentTrackId);
  const next = AMBIENT_TRACKS[(idx + 1) % AMBIENT_TRACKS.length];
  setAmbientTrackId(next.id);
  return next;
};

// ---------- Toggles ----------
export const setSfxEnabled = (val) => {
  sfxEnabled = !!val;
  try { window.localStorage.setItem(SFX_KEY, sfxEnabled ? "true" : "false"); } catch { /* ignore */ }
};
export const isSfxEnabled = () => sfxEnabled;

export const setMusicEnabled = (val) => {
  musicEnabled = !!val;
  try { window.localStorage.setItem(MUSIC_KEY, musicEnabled ? "true" : "false"); } catch { /* ignore */ }
  if (!musicEnabled) stopAmbient(1.5);
  else startAmbient();
};
export const isMusicEnabled = () => musicEnabled;

// Backwards-compat aliases used by older code
export const setEnabled = setSfxEnabled;
export const isEnabled = isSfxEnabled;

export const primeAudio = () => { getCtx(); };
